import { ThemeEvents, CartUpdatedEvent } from '@theme/events';
import { processGifts, extractCartItemCollections } from '@theme/auto-gift-processor';

class CartDrawer extends HTMLElement {
  #rendering = false;

  constructor() {
    super();
    this.checkoutButton = null;
    this.openCartIcons = null;

    this.backdropClickHandler = this.#onBackdropClick.bind(this);
    this.checkoutButtonClickHandler = this.#handleCheckoutButtonClick.bind(this);
    this.cartUpdateHandler = this.#handleCartUpdate.bind(this);
    this.openHandler = this.open.bind(this);
    this.closeHandler = this.close.bind(this);
  }

  connectedCallback() {
    this.checkoutButton = this.querySelector('[data-ref="checkout-button"]');

    this.addEventListener('click', this.backdropClickHandler);

    this.checkoutButton.addEventListener('click', this.checkoutButtonClickHandler);

    document.addEventListener(ThemeEvents.cartUpdated, this.cartUpdateHandler);

    document.addEventListener(ThemeEvents.cartOpen, this.openHandler);
    document.addEventListener(ThemeEvents.cartClose, this.closeHandler);

    // Specific logic for Shopify Theme Editor
    if (Shopify.designMode) {
      document.addEventListener('shopify:section:unload', this.#onSectionUnload);
      if (window.theme?.isCartDrawerOpen) {
        this.open();
        window.theme.isCartDrawerOpen = false; // Reset the flag
      }
    }
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartClose, this.closeHandler);
    document.removeEventListener(ThemeEvents.cartOpen, this.openHandler);

    document.removeEventListener(ThemeEvents.cartUpdated, this.cartUpdateHandler);

    this.checkoutButton.removeEventListener('click', this.checkoutButtonClickHandler);

    this.removeEventListener('click', this.backdropClickHandler);

    // Specific logic for Shopify Theme Editor
    if (Shopify.designMode) {
      document.removeEventListener('shopify:section:unload', this.#onSectionUnload);
    }
  }

  #onSectionUnload = (event) => {
    if (event.detail.sectionId !== this.dataset.sectionId) return;

    window.theme = window.theme || {};
    window.theme.isCartDrawerOpen = this.isOpen();
  };

  #handleCheckoutButtonClick(event) {
    event.target.classList.add('is-loading');
  }

  #handleCartUpdate(event) {
    this.renderCartContents(event.detail.data);
  }

  #onBackdropClick(event) {
    if (event.target.closest('dialog[open]')) return;

    const rect = this.querySelector('.cart-drawer__inner').getBoundingClientRect();
    const isInDialog = rect.top <= event.clientY && event.clientY <= rect.top + rect.height && rect.left <= event.clientX && event.clientX <= rect.left + rect.width;

    if (!isInDialog) {
      this.close();
    }
  }

  open() {
    this.classList.remove('is-closing');
    this.classList.add('is-open');
    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.add('is-closing');

    setTimeout(() => {
      this.classList.remove('is-open', 'is-closing');
      document.body.classList.remove('overflow-hidden');
    }, 125);
  }

  isOpen() {
    return this.classList.contains('is-open');
  }

  #updateAutoGiftsMapping(autoGiftsHtml) {
    if (!autoGiftsHtml) return;

    const parsed = new DOMParser().parseFromString(autoGiftsHtml, 'text/html');
    const newScript = parsed.querySelector('[data-ref="cart-item-collections"]');
    const currentScript = document.querySelector('[data-ref="cart-item-collections"]');
    if (newScript && currentScript) currentScript.textContent = newScript.textContent;
  }

  #applyState(parsedState) {
    const sectionId = this.dataset.sectionId;
    const html = parsedState.sections?.[sectionId];
    if (html) {
      const newCartDrawer = new DOMParser().parseFromString(html, 'text/html');
      const newInner = newCartDrawer.querySelector('.cart-drawer__inner');
      const currentCartDrawer = this.querySelector('.cart-drawer__inner');
      if (newInner && currentCartDrawer) currentCartDrawer.innerHTML = newInner.innerHTML;
    }

    this.#updateAutoGiftsMapping(parsedState.sections?.['auto-gifts']);

    if (parsedState.itemCount == 0) {
      this.classList.add('cart-drawer--empty');
    } else if (typeof parsedState.itemCount === 'number') {
      this.classList.remove('cart-drawer--empty');
    }

    this.#syncCartIconCount(parsedState);
  }

  async renderCartContents(parsedState) {
    if (this.#rendering) return;
    this.#rendering = true;

    try {
      const sectionId = this.dataset.sectionId;

      // 1) Affichage immediat du panier
      this.#applyState(parsedState);

      // 2) Verification du cadeau en arriere-plan, puis mise a jour si besoin
      const newAutoGiftsHtml = parsedState.sections?.['auto-gifts'];
      const newAutoGifts = newAutoGiftsHtml ? new DOMParser().parseFromString(newAutoGiftsHtml, 'text/html') : null;
      const newCollections = newAutoGifts ? extractCartItemCollections(newAutoGifts) : {};

      const giftResult = await processGifts(sectionId, newCollections);

      if (giftResult?.sections) {
        this.#applyState({ ...parsedState, sections: { ...parsedState.sections, ...giftResult.sections }, itemCount: giftResult.itemCount ?? parsedState.itemCount });
      }
    } catch (error) {
      console.error('CartDrawer: render failed', error);
    } finally {
      this.#rendering = false;
    }
  }

  #syncCartIconCount(parsedState) {
    const cartIcon = document.querySelector('cart-icon');
    if (!cartIcon) return;

    const countEl = cartIcon.querySelector('[data-ref="cart-count"]');
    if (!countEl) return;

    if (typeof parsedState.itemCount === 'number') {
      countEl.textContent = parsedState.itemCount;
      return;
    }

    cartIcon.ensureCartCountIsCorrect?.();
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

// Lit la configuration des cadeaux (blocs auto-gift) pour un produit donne
function findGiftConfig(productHandle) {
  for (const el of document.querySelectorAll('auto-gift')) {
    const configEl = el.querySelector('[data-ref="gift-config"]');
    if (!configEl) continue;
    try {
      const config = JSON.parse(configEl.textContent);
      const product = (config.products || []).find((p) => p.handle === productHandle);
      if (product) {
        return { blockId: el.dataset.blockId, giftBadge: config.giftBadge || '', product };
      }
    } catch (error) {
      console.error('CartDrawer: invalid gift config', error);
    }
  }
  return null;
}

class CartDrawerItem extends HTMLElement {
  constructor() {
    super();

    this.removeItemButton = null;
    this.quantitySelectorInput = null;
    this.giftVariantSelect = null;
    this.giftConfig = null;
    this.currentGiftVariantId = null;
    this.itemRemoveHandler = this.#handleItemRemove.bind(this);
    this.quantityInputChangeHandler = this.#handleQuantityInputChange.bind(this);
    this.giftVariantChangeHandler = this.#handleGiftVariantChange.bind(this);
  }

  connectedCallback() {
    this.removeItemButton = this.querySelector('[data-ref="remove-item"]');
    this.quantitySelectorInput = this.querySelector('[data-ref="quantity-selector-input"]');

    this.removeItemButton?.addEventListener('click', this.itemRemoveHandler);
    this.quantitySelectorInput?.addEventListener('change', this.quantityInputChangeHandler);

    if (this.dataset.isGift === 'true') this.#renderGiftVariantSelect();
  }

  disconnectedCallback() {
    this.quantitySelectorInput?.removeEventListener('change', this.quantityInputChangeHandler);
    this.removeItemButton?.removeEventListener('click', this.itemRemoveHandler);
    this.giftVariantSelect?.removeEventListener('change', this.giftVariantChangeHandler);
  }

  #renderGiftVariantSelect() {
    if (this.querySelector('[data-ref="gift-line-variant"]')) return;

    this.giftConfig = findGiftConfig(this.dataset.productHandle);
    const variants = this.giftConfig?.product?.variants || [];
    if (variants.length < 2) return;

    const optionsEl = this.querySelector('.cart-drawer__item-options');
    const optionsText = optionsEl ? optionsEl.textContent.replace(/\s+/g, ' ').trim() : '';
    const buttonVariantId = parseInt(this.querySelector('[data-variant-id]')?.dataset.variantId, 10);

    const select = document.createElement('select');
    select.className = 'select select--small';
    select.dataset.ref = 'gift-line-variant';
    select.setAttribute('aria-label', this.giftConfig.product.title || 'Variante');

    for (const variant of variants) {
      const option = document.createElement('option');
      option.value = variant.id;
      option.textContent = variant.title;
      const titleMatch = optionsText && variant.title.replace(/\s*\/\s*/g, ', ') === optionsText;
      if (variant.id === buttonVariantId || (!buttonVariantId && titleMatch)) {
        option.selected = true;
        this.currentGiftVariantId = variant.id;
      }
      select.appendChild(option);
    }

    if (!this.currentGiftVariantId) this.currentGiftVariantId = parseInt(select.value, 10);

    const wrapper = document.createElement('div');
    wrapper.className = 'select-wrapper';
    wrapper.style.maxWidth = '180px';
    wrapper.appendChild(select);

    const icon = document.querySelector('auto-gift .select-wrapper__icon');
    if (icon) wrapper.appendChild(icon.cloneNode(true));

    this.querySelectorAll('.cart-drawer__item-options').forEach((el) => (el.style.display = 'none'));
    this.querySelector('.cart-drawer__item-content')?.appendChild(wrapper);

    this.giftVariantSelect = select;
    select.addEventListener('change', this.giftVariantChangeHandler);
  }

  async #addGift(variantId, quantity, properties, sections) {
    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity, properties }], sections }),
    });
    const data = await response.json();
    if (!response.ok || data.status >= 400 || data.errors) throw new Error(data.description || data.message || 'add failed');
    return data;
  }

  async #handleGiftVariantChange() {
    const newVariantId = parseInt(this.giftVariantSelect.value, 10);
    if (!newVariantId || !this.giftConfig || newVariantId === this.currentGiftVariantId) return;

    const drawer = this.closest('cart-drawer');
    const sections = [drawer.dataset.sectionId, 'auto-gifts'].join(',');
    const quantity = parseInt(this.dataset.quantity, 10) || 1;
    const oldVariantId = this.currentGiftVariantId;

    const properties = { _is_gift: 'true', _gift_block_id: this.giftConfig.blockId };
    if (this.giftConfig.giftBadge.trim() !== '') properties._gift_badge = this.giftConfig.giftBadge.trim();

    this.#enableLoading();

    try {
      // 1) Retire l'ancienne ligne cadeau
      const removeResponse = await fetch(Theme.routes.cart_change_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: parseInt(this.dataset.index, 10), quantity: 0 }),
      });
      if (!removeResponse.ok) throw new Error('remove failed');

      // 2) Ajoute la nouvelle variante (meme methode que l'ajout automatique du cadeau)
      let data;
      try {
        data = await this.#addGift(newVariantId, quantity, properties, sections);
      } catch (addError) {
        // Si la nouvelle variante echoue, on remet l'ancienne pour ne pas perdre le cadeau
        console.warn('CartDrawer: gift variant unavailable, restoring previous one', addError);
        data = oldVariantId ? await this.#addGift(oldVariantId, quantity, properties, sections) : null;
      }

      const cart = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());

      document.dispatchEvent(
        new CartUpdatedEvent(
          {},
          {
            itemCount: cart.item_count,
            source: 'cart-drawer-gift-variant',
            sections: data?.sections || {},
          },
        ),
      );
    } catch (error) {
      console.error('CartDrawer: failed to change gift variant', error);
    } finally {
      // Securite : la ligne ne reste jamais grisee
      this.#disableLoading();
    }
  }

  #handleItemRemove(event) {
    event.preventDefault();
    this.#updateLineItemQuantity(this.dataset.index, 0);
  }

  #handleQuantityInputChange(event) {
    event.preventDefault();
    this.#updateLineItemQuantity(this.dataset.index, this.quantitySelectorInput.value);
  }

  #updateLineItemQuantity(line, quantity) {
    this.#enableLoading();

    const body = JSON.stringify({
      line,
      quantity,
      sections: [this.closest('cart-drawer').dataset.sectionId, 'auto-gifts'].join(','),
      sections_url: window.location.pathname,
    });

    fetch(Theme.routes.cart_change_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: `application/json` },
      ...{ body },
    })
      .then((response) => {
        return response.text();
      })
      .then((responsetext) => {
        const parsedResponseText = JSON.parse(responsetext);

        if (parsedResponseText.errors) {
          const message = Array.isArray(parsedResponseText.errors) ? parsedResponseText.errors.join(', ') : String(parsedResponseText.errors);
          document.dispatchEvent(new CustomEvent('toast:open', { detail: { type: 'error', message: message } }));
          return;
        }

        document.dispatchEvent(
          new CartUpdatedEvent(
            {},
            {
              itemCount: parsedResponseText.item_count,
              source: 'cart-drawer-item',
              sections: parsedResponseText.sections,
            },
          ),
        );
      })
      .catch((e) => {
        console.error(e);
        this.#disableLoading();
      })
      .finally(() => {
        this.#disableLoading();
      });
  }

  #enableLoading() {
    this.classList.add('cart-drawer__item--loading');
  }

  #disableLoading() {
    this.classList.remove('cart-drawer__item--loading');
  }
}

if (!customElements.get('cart-drawer-item')) {
  customElements.define('cart-drawer-item', CartDrawerItem);
}
